// File: src/components/nav/AppSidebar.tsx
import React, { type ComponentType } from "react";
import logoUrl from "../../assets/sonne-logo.png";
import { Sidebar } from "../ui/sidebar";
import type { MenuItem, TabKey } from "../../config/menu";
import { SidebarContext } from "../ui/sidebar-context";
import { ChevronDown } from "lucide-react";
import { apiUrl, authFetch, currentAuthUser, saveAuth } from "../../utils/api";
import { Avatar } from "../common/Avatar";
import { AVATAR_EVENT } from "../../utils/avatar";

const OPEN_GROUPS_KEY = "sonne_nav_open_groups_v1";

// Unterpunkte, die eingerückt unter einem Elternpunkt erscheinen. Sie klappen
// auf, sobald der Elternpunkt oder einer der Unterpunkte geöffnet ist.
const SUBNAV: Partial<Record<TabKey, TabKey[]>> = {
  qmhandbuch: ["va", "beschwerdemanagement"],
  mitarbeitende: ["fortbildungen"],
};

// Kategorien in Anzeige-Reihenfolge. Es werden nur Einträge gezeigt, die die Rolle
// tatsächlich sehen darf (kommen über `items`).
const CATEGORIES: Array<{ label: string | null; ids: TabKey[] }> = [
  { label: null, ids: ["dashboard", "adminpanel"] },
  { label: "Qualitätsmanagement", ids: ["qmhandbuch", "prozesse", "md", "iso9001", "iso15224", "pflegevisite"] },
  { label: "Organisation", ids: ["mitarbeitende", "wiki", "dokumente", "vorlagenbibliothek", "wissenswertes", "organisation"] },
  { label: "System", ids: ["fortbildungsbibliothek", "qmvorlagen", "qmhimport"] },
];

function roleLabelDe(role?: string) {
  if (role === "system_admin") return "System-Admin";
  if (role === "admin") return "Admin";
  return "Mitarbeitende/r";
}

export function AppSidebar({ items, activeItem, onItemClick }: { items: MenuItem[]; activeItem: TabKey; onItemClick: (id: TabKey) => void }) {
  const { collapsed, toggle } = React.useContext(SidebarContext);
  const select = React.useCallback((id: TabKey) => {
    onItemClick(id);
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches && !collapsed) toggle();
  }, [onItemClick, collapsed, toggle]);

  // Kategorien klappen per Klick auf die Überschrift auf/zu; die Kategorie des
  // geöffneten Bereichs ist immer offen. Zustand bleibt pro Browser erhalten.
  const [openGroups, setOpenGroups] = React.useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) || "[]")); } catch { return new Set(); }
  });
  const toggleGroup = (label: string) => setOpenGroups((cur) => {
    const next = new Set(cur);
    if (next.has(label)) next.delete(label); else next.add(label);
    try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify([...next])); } catch { /* ignore */ }
    return next;
  });
  const activeParent = (Object.keys(SUBNAV) as TabKey[]).find((p) => p === activeItem || SUBNAV[p]!.includes(activeItem));
  const activeGroup = CATEGORIES.find((c) => c.ids.includes(activeItem) || (activeParent != null && c.ids.includes(activeParent)))?.label;

  // Profilbild: beim Start einmal aktualisieren (z. B. vom Admin gesetzt) und bei Änderung neu zeichnen.
  const [, setAvatarTick] = React.useState(0);
  React.useEffect(() => {
    const rerender = () => setAvatarTick((n) => n + 1);
    window.addEventListener(AVATAR_EVENT, rerender);
    authFetch(apiUrl("/api/v1/auth/me")).then((r) => (r.ok ? r.json() : null)).then((data) => {
      const me = currentAuthUser();
      const url = data?.user?.avatar_url ?? null;
      if (me && data?.user && (me.avatar_url ?? null) !== url) { saveAuth({ ...me, avatar_url: url }); rerender(); }
    }).catch(() => { /* offline: gespeicherten Stand zeigen */ });
    return () => window.removeEventListener(AVATAR_EVENT, rerender);
  }, []);

  const byId = new Map(items.map((item) => [item.id, item] as const));
  const childIds = new Set<TabKey>(Object.values(SUBNAV).flat());

  const user = currentAuthUser();
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "Angemeldet";

  const renderIcon = (item: MenuItem) => {
    const Icon = item.icon as ComponentType<React.SVGProps<SVGSVGElement>>;
    return <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />;
  };

  const renderItem = (item: MenuItem) => {
    const active = item.id === activeItem;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => select(item.id)}
        aria-current={active ? "page" : undefined}
        aria-label={item.title}
        title={item.title}
        data-tour={`nav-${item.id}`}
        className={[
          "flex w-full items-center gap-[11px] rounded-lg px-[11px] py-[9px] text-left text-[.86rem] font-medium transition-colors",
          collapsed ? "justify-center" : "",
          active
            ? "bg-[var(--teal)] text-white font-semibold shadow-[0_2px_6px_rgba(0,159,181,.25)]"
            : "text-[var(--ink-mid)] hover:bg-[var(--line-soft)] hover:text-[var(--ink)]",
        ].join(" ")}
      >
        {renderIcon(item)}
        {!collapsed && <span className="truncate">{item.title}</span>}
        {!collapsed && hasKids(item.id) && (
          <ChevronDown className={`ml-auto h-3.5 w-3.5 shrink-0 opacity-70 transition-transform ${activeParent === item.id ? "" : "-rotate-90"}`} />
        )}
      </button>
    );
  };

  const hasKids = (id: TabKey) => (SUBNAV[id] || []).some((kid) => byId.has(kid));

  const renderSub = (parentId: TabKey) => {
    if (collapsed || activeParent !== parentId) return null;
    const kids = (SUBNAV[parentId] || []).map((id) => byId.get(id)).filter(Boolean) as MenuItem[];
    if (kids.length === 0) return null;
    return (
      <div className="my-[2px] flex flex-col gap-[1px] pl-[28px]">
        {kids.map((kid) => {
          const active = kid.id === activeItem;
          return (
            <button
              key={kid.id}
              type="button"
              onClick={() => select(kid.id)}
              aria-current={active ? "page" : undefined}
              aria-label={kid.title}
              title={kid.title}
              data-tour={`nav-${kid.id}`}
              className={[
                "rounded-md px-[10px] py-[7px] text-left text-[.8rem] transition-colors",
                active
                  ? "bg-[var(--teal-light)] font-semibold text-[var(--teal-dark)]"
                  : "text-[var(--ink-mid)] hover:bg-[var(--line-soft)] hover:text-[var(--ink)]",
              ].join(" ")}
            >
              {kid.title}
            </button>
          );
        })}
      </div>
    );
  };

  // Ungruppierte, aber sichtbare Einträge am Ende auffangen (z. B. Profil, neue Tabs).
  const placed = new Set<TabKey>([...CATEGORIES.flatMap((c) => c.ids), ...childIds, "profil"]);
  const leftover = items.filter((i) => !placed.has(i.id));

  return (
    <Sidebar>
      <div className="flex items-center gap-[11px] border-b border-[var(--line-soft)] px-[18px] py-[18px]">
        <img src={logoUrl} alt="Sonne QM" className="h-9 w-9 shrink-0 rounded-[10px] object-contain" />
        {!collapsed && (
          <div className="min-w-0">
            <strong className="block truncate text-[.95rem] font-extrabold tracking-tight text-[var(--ink)]">Sonne QM</strong>
            <span className="block truncate text-[.72rem] text-[var(--ink-muted)]">Qualitätsmanagement</span>
          </div>
        )}
      </div>

      <nav className="flex flex-1 flex-col gap-[2px] overflow-y-auto px-[10px] py-3">
        {CATEGORIES.map((cat, ci) => {
          const catItems = cat.ids.map((id) => byId.get(id)).filter(Boolean) as MenuItem[];
          if (catItems.length === 0) return null;
          const open = collapsed || !cat.label || cat.label === activeGroup || openGroups.has(cat.label);
          const groupActive = cat.label != null && cat.label === activeGroup;
          return (
            <React.Fragment key={cat.label ?? `cat-${ci}`}>
              {cat.label && !collapsed && (
                <button
                  type="button"
                  onClick={() => toggleGroup(cat.label!)}
                  aria-expanded={open}
                  disabled={groupActive}
                  title={groupActive ? undefined : open ? "Zuklappen" : "Aufklappen"}
                  className="mt-[8px] flex w-full items-center gap-2 rounded-md px-[10px] py-[6px] text-left text-[.66rem] font-bold uppercase tracking-[.1em] text-[var(--ink-muted)] transition-colors hover:text-[var(--ink)] disabled:cursor-default disabled:hover:text-[var(--ink-muted)]"
                >
                  <span className="flex-1">{cat.label}</span>
                  {!groupActive && <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />}
                </button>
              )}
              {open && catItems.map((item) => (
                <React.Fragment key={item.id}>
                  {renderItem(item)}
                  {renderSub(item.id)}
                </React.Fragment>
              ))}
            </React.Fragment>
          );
        })}
        {leftover.length > 0 && (
          <>
            {!collapsed && <div className="px-[10px] pb-[6px] pt-[14px] text-[.66rem] font-bold uppercase tracking-[.1em] text-[var(--ink-muted)]">Weiteres</div>}
            {leftover.map(renderItem)}
          </>
        )}
      </nav>

      <div className="border-t border-[var(--line-soft)] p-3">
        <button
          type="button"
          onClick={() => byId.has("profil") && select("profil")}
          className={`flex w-full items-center gap-[10px] rounded-lg ${collapsed ? "justify-center" : ""} bg-[var(--surface)] px-[10px] py-[9px] text-left transition-colors hover:bg-[var(--line-soft)]`}
          title={name}
        >
          <Avatar url={user?.avatar_url} name={name} size={32} />
          {!collapsed && (
            <span className="min-w-0">
              <strong className="block truncate text-[.8rem] font-semibold leading-tight text-[var(--ink)]">{name}</strong>
              <span className="block truncate text-[.7rem] text-[var(--ink-muted)]">{roleLabelDe(user?.role)}</span>
            </span>
          )}
        </button>
      </div>
    </Sidebar>
  );
}

export default AppSidebar;
